
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export const useAuthCheck = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          // Not logged in at all
          navigate('/login');
          return;
        }
        
        // Check if email is verified
        if (!session.user.email_confirmed_at) {
          toast({
            title: "Email verification required",
            description: "Please verify your email before accessing the dashboard.",
            variant: "destructive",
          });
          navigate('/account-verification-needed');
          return;
        }
        
        // Check if user has an active subscription - get all active subscriptions
        const { data: subscriptions, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('status', 'active');
          
        if (error) {
          console.error('Error checking subscription:', error);
        }
        
        console.log('Checking subscription for user:', session.user.id);
        console.log('Active subscriptions found:', subscriptions?.length);
        
        // Check if there's at least one active subscription
        if (!subscriptions || subscriptions.length === 0) {
          toast({
            title: "Subscription required",
            description: "Please choose a subscription plan to access the dashboard.",
            variant: "destructive",
          });
          navigate('/pricing');
          return;
        } else {
          console.log('Found active subscription');
        }
        
      } catch (error) {
        console.error('Auth check error:', error);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
    
    // Show success message if payment was successful
    if (location.search.includes('payment=success')) {
      toast({
        title: "Payment successful!",
        description: "Thank you for your purchase. You now have full access to the dashboard.",
      });
      
      // Clear the URL parameters
      navigate('/tracks', { replace: true });
    }
  }, [navigate, location.search, toast]);

  return { isLoading };
};
